import React, { useState } from 'react';
import PatientControlButton from 'components/atoms/patentControlButton';
import ControlButtonDropdown from 'components/atoms/controlPanelDropdownButton/controlButtonDropdown';
import { useTranslation } from 'react-i18next';
import CallControlIcon from 'components/atoms/icons/callControlIcon';
import { useCallButtons } from 'infrastructure/hooks/control-panel';
import ManualEnterCall from 'components/organisms/controlPanel/manualEnteredCallModal';
import useUser from 'utils/useUser';
import BaseGrid from 'components/atoms/base-grid';
import { useAccess } from 'infrastructure/hooks/access/useAccess';

import type { ModuleType } from 'infrastructure/enums';

const ToggleButton: React.FC<{ label: string; disabled?: boolean }> = (
  props,
) => {
  const { label, disabled } = props;
  return (
    <PatientControlButton
      label={label}
      Icon={CallControlIcon}
      disabled={disabled}
    />
  );
};

interface IProps {
  module: ModuleType;
  patientGuid: string;
}

const CallButtonsController: React.FC<IProps> = (props) => {
  const { module, patientGuid } = props;
  const { t } = useTranslation();

  const [isRpmManualOpen, setIsRpmManualOpen] = useState(false);
  const [isCcmManualOpen, setIsCcmManualOpen] = useState(false);

  const { userType, roleNames, timezone, isMobileDevice } = useUser();
  const {
    patientInfo,
    doctorInfo,
    isRpmModule,
    isCcmModule,
    isCombinedModule,
    isBlockCcmModule,
    isBlockRpmModule,
    isCcmActionDisabled,
  } = useAccess({
    module,
    patientGuid,
  });

  const { callButtons, callButtonsAlert } = useCallButtons({
    patientGuid,
    userType,
    doNotCall: patientInfo?.doNotCall,
    emergencyContactNumber: patientInfo?.emergencyContactNumber ?? '',
    patientHomeNumber: patientInfo?.homeNumber ?? '',
    patientPhone: patientInfo?.loginInfo?.phone ?? '',
    doctorPhone: doctorInfo?.phone,
    setRpmManualEnteredCallPopupOpen: setIsRpmManualOpen,
    setCcmManualEnteredCallPopupOpen: setIsCcmManualOpen,
    roleNames,
    patientTimeZone: timezone,
    module,
  });

  const getLabel = (prefix: string) => {
    const label = t('labels.call');
    if (isCombinedModule) return `${prefix} ${label}`;
    return label;
  };

  const columns = isMobileDevice || !isCombinedModule ? 1 : 2;

  return (
    <>
      <BaseGrid columns={columns}>
        {(isRpmModule || isCombinedModule) && (
          <ControlButtonDropdown
            toggleBtn={
              <ToggleButton
                label={getLabel(t('labels.rpm'))}
                disabled={isCcmActionDisabled || isBlockRpmModule}
              />
            }
            options={callButtons}
            isRpm
          />
        )}

        {(isCcmModule || isCombinedModule) && (
          <ControlButtonDropdown
            toggleBtn={
              <ToggleButton
                label={getLabel(t('labels.ccm'))}
                disabled={isCcmActionDisabled || isBlockCcmModule}
              />
            }
            options={callButtons}
            isRpm={false}
          />
        )}
      </BaseGrid>
      <ManualEnterCall
        isOpen={isRpmManualOpen}
        toggle={setIsRpmManualOpen}
        patientGuid={patientGuid}
        isRpmPanel
      />
      <ManualEnterCall
        isOpen={isCcmManualOpen}
        toggle={setIsCcmManualOpen}
        patientGuid={patientGuid}
        isRpmPanel={false}
      />
      {callButtonsAlert}
    </>
  );
};

export default CallButtonsController;
